import * as React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { useLingui } from "@lingui/react";

import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface SegmentedToggleOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Shown as a small badge after the label, e.g. open items in that scope. */
  count?: number;
  disabled?: boolean;
}

export interface SegmentedToggleProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedToggleOption<T>[];
  "aria-label": string;
  size?: "sm" | "md";
  className?: string;
}

const itemSizes = {
  sm: "h-7 px-2.5 text-xs",
  md: "h-8 px-3 text-sm",
} as const;

/**
 * A single-choice switch between a few views or scopes (Day/Week, Open/All).
 * Radix exposes it as a radiogroup with arrow-key navigation. Exactly one
 * option is always selected: clicking the active one keeps it, because an
 * empty value would leave the view showing nothing.
 */
export function SegmentedToggle<T extends string>({
  value,
  onValueChange,
  options,
  "aria-label": ariaLabel,
  size = "md",
  className,
}: SegmentedToggleProps<T>) {
  const { i18n } = useLingui();

  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={(next) => {
        if (next) onValueChange(next as T);
      }}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-md bg-muted p-1 text-muted-foreground",
        className
      )}
    >
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-sm font-medium ring-offset-background transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm",
            itemSizes[size]
          )}
        >
          {option.label}
          {option.count !== undefined && (
            <span className="min-w-5 rounded-full bg-muted-foreground/15 px-1.5 text-center text-xs tabular-nums">
              {formatNumber(option.count, i18n.locale)}
            </span>
          )}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
